import React, { useEffect, useState, useCallback } from "react";
import { Button, Modal } from "react-bootstrap";
import CourseStructureService from "../../Services/CourseStructureService";
import toast from "react-hot-toast";
import AddCourseStructureModal from "./AddCourseStructure";
import EditCourseStructureModal from "./EditCourseStructure";

function MainCourseStructure({ data, programId }) {
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [courseStructure, setCourseStructure] = useState();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCourseStructure, setSelectedCourseStructure] = useState(null);
  const getCourseStructure = useCallback(async () => {
    try {
      setLoading(true);
      const response = await CourseStructureService.getCourseStructure(
        programId
      );

      if (response.data.success) {
        setCourseStructure(response.data.data);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.log("Error fetching course structure", error);
      toast.error("An error occurred while fetching course structure");
    } finally {
      setLoading(false);
    }
  }, [programId]);

  useEffect(() => {
    if (show) {
      getCourseStructure();
    }
  }, [programId, show, getCourseStructure]);

  const handleAddSuccess = () => {
    getCourseStructure();
  };

  const handleEditSuccess = () => {
    getCourseStructure();
  };

  const handleEditClick = () => {
    if (courseStructure && courseStructure.length > 0) {
      setSelectedCourseStructure(courseStructure[0]);
      setShowEditModal(true);
    }
  };

  return (
    <>
      <Button
        variant="outline-secondary"
        className="w-100 rounded-pill me-1"
        style={{ position: "relative" }}
        title="Course Structure"
        onClick={() => setShow(true)}
      >
        <i className="bi bi-file-earmark-text"></i>
      </Button>

      <Modal show={show} onHide={() => setShow(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-diagram-3 me-2"></i>
            Course Structure
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 fs-6">Loading course structure...</p>
            </div>
          ) : courseStructure && courseStructure.length > 0 ? (
            <div className="course-structure-container">
              {courseStructure.map((item, index) => (
                <div key={item.id || index} className="mb-4">
                  {/* Description Section */}
                  <div className="mb-3">
                    <h5 className="text-primary mb-2">
                      <i className="bi bi-info-circle me-2"></i>
                      Course Description
                    </h5>
                    <p className="text-muted mb-2" style={{ fontSize: "14px" }}>
                      {item.description}
                    </p>
                    {item.subDescription && (
                      <p className="text-muted" style={{ fontSize: "14px" }}>
                        {item.subDescription}
                      </p>
                    )}
                  </div>

                  {/* Structure Section */}
                  {item.structure && (
                    <div>
                      <h5
                        className="text-primary mb-3"
                        style={{ fontSize: "16px" }}
                      >
                        <i className="bi bi-diagram-3 me-2"></i>
                        Course Structure
                      </h5>
                      <div className="row">
                        {Object.entries(item.structure).map(
                          ([yearKey, yearData]) => (
                            <div key={yearKey} className="col-md-6 mb-3">
                              <div className="card h-100 border-0 shadow-sm">
                                <div className="card-header bg-primary text-white">
                                  <h5
                                    className="mb-0"
                                    style={{ fontSize: "14px" }}
                                  >
                                    <i className="bi bi-calendar-year me-2"></i>
                                    {yearKey.replace("year", "Year ")} -{" "}
                                    {yearData.title}
                                  </h5>
                                </div>
                                <div className="card-body">
                                  <div className="courses-list">
                                    {yearData.courses &&
                                    yearData.courses.length > 0 ? (
                                      yearData.courses.map(
                                        (course, courseIndex) => (
                                          <div
                                            key={courseIndex}
                                            className="course-item mb-2"
                                          >
                                            <div className="d-flex align-items-center">
                                              <i className="bi bi-book me-2 text-primary fs-5"></i>
                                              <span
                                                className="text-dark"
                                                style={{ fontSize: "14px" }}
                                              >
                                                {course}
                                              </span>
                                            </div>
                                          </div>
                                        )
                                      )
                                    ) : (
                                      <p
                                        className="text-muted mb-0"
                                        style={{ fontSize: "14px" }}
                                      >
                                        <i className="bi bi-exclamation-circle me-2"></i>
                                        No courses available
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4">
              <i
                className="bi bi-file-earmark-text text-muted"
                style={{ fontSize: "3rem" }}
              ></i>
              <p className="text-muted mt-2" style={{ fontSize: "14px" }}>
                No course structure available
              </p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {courseStructure && courseStructure.length === 0 && (
            <Button variant="primary" onClick={() => setShowAddModal(true)}>
              <i className="bi bi-plus-circle me-2"></i>
              Add Course Structure
            </Button>
          )}
          {courseStructure && courseStructure.length > 0 && (
            <Button variant="primary" onClick={handleEditClick}>
              <i className="bi bi-pencil-square me-2"></i>
              Edit Course Structure
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <AddCourseStructureModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        programId={programId}
        onSuccess={handleAddSuccess}
      />

      <EditCourseStructureModal
        show={showEditModal}
        programId={programId}
        onHide={() => setShowEditModal(false)}
        courseStructureData={selectedCourseStructure}
        onSuccess={handleEditSuccess}
      />
    </>
  );
}

export default MainCourseStructure;
